import { Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { FAIRS_CONFIGURATION, FairsConfiguration } from '@planone/bau365/data-access-fair-data';
import * as SnowplowActions from '@planone/shared/feature-tracking-snowplow';
import { selectRouteParams } from '@planone/shared/util-ngrx';
import { filter, of, switchMap, withLatestFrom } from 'rxjs';

@Injectable()
export class SnowplowEffects {
  pageView$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SnowplowActions.pageView),
      filter((action) => !action.enriched),
      withLatestFrom(this.store.select(selectRouteParams)),
      switchMap(([action, params]) => {
        let titleContext: string | undefined = undefined;

        if (
          Object.keys(params).includes('fair_id') &&
          Object.keys(this.fairsConfiguration.fairs).includes(params['fair_id'])
        ) {
          titleContext = this.fairsConfiguration.fairs[params['fair_id']].name;
        }

        return of(
          SnowplowActions.pageView({
            ...this.recursiveReplace('context', params['fair_id'] ?? 'cross-fair', action),
            ...(titleContext && { title_context: titleContext }),
            enriched: true,
          })
        );
      })
    )
  );

  selfDescribingEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SnowplowActions.selfDescribingEvent),
      filter((action) => !action.enriched),
      withLatestFrom(this.store.select(selectRouteParams)),
      switchMap(([action, params]) =>
        of(
          SnowplowActions.selfDescribingEvent({
            ...this.recursiveReplace('context', params ? params['fair_id'] ?? 'cross-fair' : 'cross-fair', action),
            enriched: true,
          })
        )
      )
    )
  );

  linkClick$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SnowplowActions.linkClick),
      filter((action) => !action.enriched),
      withLatestFrom(this.store.select(selectRouteParams)),
      switchMap(([action, params]) =>
        of(
          SnowplowActions.linkClick({
            ...this.recursiveReplace('context', params ? params['fair_id'] ?? 'cross-fair' : 'cross-fair', action),
            enriched: true,
          })
        )
      )
    )
  );

  constructor(
    @Inject(FAIRS_CONFIGURATION) private fairsConfiguration: FairsConfiguration,
    private readonly actions$: Actions,
    private readonly store: Store
  ) {}

  private recursiveReplace(key: any, value: any, subject: any) {
    if (typeof subject === 'object') {
      for (const objectKey in subject) {
        if (typeof subject[objectKey] === 'object') {
          if (Array.isArray(subject[objectKey])) {
            const array: any[] = [];

            for (const arrayValue of subject[objectKey]) {
              if (typeof subject[objectKey] === 'object') {
                array.push(this.recursiveReplace(key, value, arrayValue));
              } else {
                array.push(arrayValue);
              }
            }

            subject = {
              ...subject,
              [objectKey]: array,
            };
          } else {
            subject = {
              ...subject,
              [objectKey]: this.recursiveReplace(key, value, subject[objectKey]),
            };
          }
        } else if (objectKey === key) {
          subject = {
            ...subject,
            [objectKey]: value,
          };
        }
      }
    }

    return subject;
  }
}
